import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import qs from "qs";
import Swal from "sweetalert2";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ID } from "material-react-table/locales/id";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
    hidePreloader,
    showPreloader,
    twoDecimalFormat,
    decimalFormat,
    dateSortingFn,
    datePeriod,
} from "../../library/helper";

import DateCell from "../../components/DateCell";
import PageHeader from "../../components/PageHeader";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";

export default function Emission() {
    const { t, i18n } = useTranslation();
    const account = useSelector((state) => state.account.value);

    const [selectedClient, setSelectedClient] = useState("");
    const [optionClient, setOptionClient] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [columnVisibility, setColumnVisibility] = useState({
        record_date: true,
        record_location: true,
        record_category: true,
        record_assets: true,
        record_total: true,
        record_amount: true,
        record_uuid: false,
        record_client_name: true,
    });

    // ---- parse user roles from account.user_roles (stringified array or array) ----
    const userRoles = useMemo(() => {
        const raw = account?.user_roles;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error("Failed to parse user_roles", e);
                return [];
            }
        }
        return [];
    }, [account]);

    const hasRole = (role) => userRoles.includes(role);

    // helper to safely escape HTML in notes / names
    const escapeHtml = (str = "") =>
        String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const showRecordHistory = async (recordUuid) => {
        try {
            const response = await axios({
                withCredentials: true,
                method: "GET",
                url: `${process.env.REACT_APP_BASE_SERVER}/emission/log`,
                params: { recordUuid },
                headers: {
                    "content-type": "application/json",
                },
            });

            if (!response?.data?.ok || !Array.isArray(response.data.data)) {
                Swal.fire({
                    icon: "info",
                    title: "No history found",
                    text: "There is no history for this record yet.",
                    confirmButtonColor: "#3085d6",
                });
                return;
            }

            const logs = response.data.data;

            const rowsHtml = logs
                .map((log) => {
                    const createdAt = log.createdAt
                        ? new Date(log.createdAt).toLocaleString()
                        : "-";

                    return `
            <tr>
              <td>${createdAt}</td>
              <td>${escapeHtml(log.fromStatus)} → ${escapeHtml(
                        log.toStatus
                    )}</td>
              <td>${escapeHtml(log.role)}</td>
              <td>${escapeHtml(log.actorUserName || "")}</td>
              <td>${escapeHtml(log.note || "")}</td>
            </tr>
          `;
                })
                .join("");

            const tableHtml = `
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0" style="font-size: 0.9rem;">
            <thead>
              <tr>
                <th>Time</th>
                <th>Status</th>
                <th>Role</th>
                <th>User</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>
      `;

            Swal.fire({
                title: "Record History",
                html: tableHtml,
                width: "60rem",
                confirmButtonColor: "#3085d6",
            });
        } catch (e) {
            hidePreloader();
            console.error(e);
            let message = "";
            if (typeof e.response?.data?.message === "string") {
                message = e.response.data.message;
            } else if (e.message) {
                message = e.message;
            } else {
                message = "Failed to load record history, please try again later.";
            }

            Swal.fire({
                icon: "error",
                title: "Error",
                text: message,
                confirmButtonColor: "#3085d6",
            });
        }
    };

    useEffect(() => {
        showPreloader();

        const getResponse = async () => {
            try {
                const data = {};
                const options = {
                    withCredentials: true,
                    method: "POST",
                    headers: { "content-type": "application/x-www-form-urlencoded" },
                    data: qs.stringify({
                        gwp: new URLSearchParams(window.location.search).get("gwp"),
                        ...data,
                    }),
                    url: `${process.env.REACT_APP_BASE_SERVER}/emission/get_all`,
                };

                const response = await axios(options);
                if (response.data.success) {
                    const record = response.data.data;
                    setTableData(record);
                }
            } catch (e) {
                console.error(e);
            } finally {
                hidePreloader();
            }
        };

        getResponse();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setTimeout(() => {
            setColumnVisibility((prev) => ({
                ...prev,
                record_uuid: !!account,
                record_client_name: account && account["is_parent"] === 1 ? true : false,
            }));
            setIsLoading(false);
        }, 300);
    }, [account, optionClient]);

    useEffect(() => {
        const controller = new AbortController();
        const getClients = async () => {
            try {
                const response = await axios({
                    withCredentials: true,
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    url: `${process.env.REACT_APP_BASE_SERVER}/client/get_all`,
                    signal: controller.signal,
                });
                if (response?.data?.ok) {
                    if (Array.isArray(response?.data?.data)) {
                        setOptionClient(response?.data?.data);
                    }
                }
            } catch (e) {
                console.error(e);
                let message = "";
                if (typeof e.response?.data?.message === "string") {
                    message = e.response.data.message;
                } else if (e.message) {
                    message = e.message;
                }
                if (e.code && e.code !== "ERR_CANCELED") {
                    Swal.fire({
                        icon: "error",
                        title: "",
                        html: `<p>${message}</p>`,
                        showCancelButton: false,
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Ok",
                    });
                }
            }
        };
        getClients();

        return () => controller.abort();
    }, []);

    const onDeleteCall = async (data) => {
        const options = {
            withCredentials: true,
            method: "POST",
            headers: { "content-type": "application/x-www-form-urlencoded" },
            data: qs.stringify(data),
            url: `${process.env.REACT_APP_BASE_SERVER}/emission/delete`,
        };
        const response = await axios(options);
        return !!response.data.success;
    };

    const onDelete = async (id) => {
        let invalidData = true;
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                const data = { id };
                const ok = await onDeleteCall(data);
                if (ok) invalidData = false;

                if (invalidData) {
                    Swal.fire({
                        title: "",
                        html: "<p>Cannot delete data, please try again later</p>",
                        showCancelButton: false,
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Ok",
                    });
                } else {
                    Swal.fire({
                        title: "Deleted",
                        text: "Your data has been deleted.",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "Ok",
                    }).then((result) => {
                        if (result.isConfirmed) window.location.reload();
                    });
                }
            }
        });
    };

    // ---- helper to change status with note ----
    const changeRecordStatus = async (recordUuid, newStatus) => {
        const { value: note } = await Swal.fire({
            title: "Add Note",
            input: "textarea",
            inputLabel: "Note (optional)",
            inputPlaceholder: "Enter a note...",
            inputAttributes: { "aria-label": "Note" },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Submit",
        });

        if (note === undefined) return;

        try {
            const response = await axios({
                withCredentials: true,
                method: "POST",
                headers: { "content-type": "application/json" },
                url: `${process.env.REACT_APP_BASE_SERVER}/emission/status`,
                data: { recordUuid, newStatus, note: note ?? "" },
            });

            if (response?.data?.success || response?.data?.ok) {
                setTableData((prev) =>
                    prev.map((r) =>
                        r.record_uuid === recordUuid ? { ...r, record_status: newStatus } : r
                    )
                );
                Swal.fire({
                    icon: "success",
                    title: "Status updated",
                    text: `Record status changed to ${newStatus}.`,
                    confirmButtonColor: "#3085d6",
                });
            } else {
                const message =
                    (typeof response?.data?.message === "string" &&
                        response.data.message) ||
                    "Failed to update status, please try again later.";
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: message,
                    confirmButtonColor: "#3085d6",
                });
            }
        } catch (e) {
            hidePreloader();
            console.error(e);
            let message = "";
            if (typeof e.response?.data?.message === "string") {
                message = e.response.data.message;
            } else if (e.message) {
                message = e.message;
            } else {
                message = "Failed to update status, please try again later.";
            }
            Swal.fire({
                icon: "error",
                title: "Error",
                text: message,
                confirmButtonColor: "#3085d6",
            });
        }
    };

    // compact actions cell (icons + dropdown)
    const RowActionsCell = ({ record, uuidValue }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const status = record.record_status;

        const canInputter = hasRole("INPUTTER");
        const canReviewer = hasRole("REVIEWER");
        const canApprover = hasRole("APPROVER");

        const workflowItems = [];

        if (["DRAFT", "REJECTED"].includes(status) && canInputter) {
            workflowItems.push({
                key: "submit-for-review",
                label: "Submit for Review",
                status: "WAITING_REVIEW",
            });
        }

        if (["WAITING_REVIEW", "UNAPPROVED"].includes(status) && canReviewer) {
            workflowItems.push({
                key: "mark-reviewed",
                label: "Mark as Reviewed",
                status: "REVIEWED",
            });
            workflowItems.push({
                key: "mark-rejected",
                label: "Reject",
                status: "REJECTED",
            });
        }

        if (status === "REVIEWED" && canApprover) {
            workflowItems.push({
                key: "approve",
                label: "Approve",
                status: "APPROVED",
            });
            workflowItems.push({
                key: "mark-unapproved",
                label: "Mark Unapproved",
                status: "UNAPPROVED",
            });
        }

        const handleOpen = (e) => setAnchorEl(e.currentTarget);
        const handleClose = () => setAnchorEl(null);

        const handleWorkflowClick = async (nextStatus) => {
            handleClose();
            await changeRecordStatus(record.record_uuid, nextStatus);
        };

        const approvalHint =
            i18n.language === "id"
                ? "Persetujuan Data"
                : "Data Approval";

        return (
            <div className="d-flex align-items-center gap-2">
                <Tooltip title={i18n.language === "id" ? "Edit" : "Edit"}>
                    <IconButton
                        size="small"
                        color="info"
                        component={Link}
                        to={"/emission/update/" + uuidValue}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* <Tooltip title={i18n.language === "id" ? "Riwayat" : "History"}>
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => showRecordHistory(record.record_uuid)}
                    >
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                </Tooltip> */}

                <Tooltip title={i18n.language === "id" ? "Hapus" : "Delete"}>
                    <IconButton
                        size="small"
                        color="warning"
                        onClick={() => onDelete(uuidValue)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* {workflowItems.length > 0 ? (
                    <>
                        <Tooltip title={`${approvalHint} (${status || "-"})`}>
                            <IconButton size="small" onClick={handleOpen}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <MenuItem disabled sx={{ opacity: 0.8 }}>
                                {i18n.language === "id" ? "Status Saat Ini" : "Current Status"}:{" "}
                                {status || "-"}
                            </MenuItem>
                            <Divider />
                            {workflowItems.map((item) => (
                                <MenuItem
                                    key={item.key}
                                    onClick={() => handleWorkflowClick(item.status)}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                ) : (

                    <Tooltip title={`${approvalHint} (${status || "-"})`}>
                        <span>
                            <IconButton size="small" disabled>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )} */}
            </div>
        );
    };

    const columns = useMemo(() => {
        const siteColumn = {
            accessorKey: "record_client_name",
            header: t("Site"),
            size: 160,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? String(value) : "";
            },
            enableColumnFilter: true,
            filterFn: "equals",
            enableSorting: false,
            enableColumnActions: false,
            enableResizing: false,
            enableHiding: true,
        };

        const baseColumns = [
            {
                header: t("emission.table.label1"),
                size: 140,
                accessorKey: "record_date",
                accessorFn: (row) =>
                    row.record_date_time ? new Date(row.record_date_time) : null,
                Cell: ({ row }) => {
                    const dateTime = row.original.record_date_time;
                    return dateTime ? <DateCell dateTime={dateTime} /> : "";
                },
                sortingFn: dateSortingFn,
                filterFn: datePeriod,
            },
            {
                accessorKey: "record_location",
                header: t("emission.table.label2"),
                size: 180,
                Cell: ({ cell }) => cell.getValue(),
            },
            {
                accessorKey: "record_category",
                header: t("emission.table.label3"),
                size: 200,
                Cell: ({ cell, row }) => (
                    <>
                        {i18n.language === "id"
                            ? row.original.record_category_id
                            : cell.getValue()}
                    </>
                ),
            },
            {
                accessorKey: "record_assets",
                header: t("emission.table.label4"),
                size: 180,
            },
            {
                accessorKey: "record_total",
                header: t("emission.table.label7"),
                size: 150,
                Cell: ({ cell }) => (
                    <div className="d-flex flex-column flex-wrap gap-2">
                        <span>
                            {twoDecimalFormat(cell.getValue(), i18n.language)} tCO
                            <sub>2</sub>e
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "record_amount",
                header: t("emission.table.label5"),
                size: 180,
                Cell: ({ row }) => (
                    <div className="d-flex flex-column flex-wrap gap-2">
                        <span>
                            {decimalFormat(row.original.record_amount, i18n.language)}{" "}
                            {row.original.record_unit}
                        </span>
                    </div>
                ),
            },
            // {
            //     accessorKey: "record_status",
            //     header: "Status",
            //     size: 140,
            // },
            {
                accessorKey: "record_uuid",
                header: t("emission.table.label6"),
                enableSorting: false,
                enableColumnFilter: false,
                enableGrouping: false,
                enableHiding: true,
                size: 260,
                Cell: ({ cell, row }) => {
                    const record = row.original;
                    const uuidValue = cell.getValue();
                    return <RowActionsCell record={record} uuidValue={uuidValue} />;
                },
            },
        ];

        return [siteColumn, ...baseColumns];
    }, [i18n.language, hasRole, userRoles, t]);

    const table = useMaterialReactTable({
        columns,
        data: tableData,

        enableGrouping: true,
        groupedColumnMode: "reorder",
        enableColumnOrdering: true,

        enableGlobalFilter: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,

        enableColumnPinning: true,
        enableStickyHeader: true,

        initialState: {
            density: "compact",
            columnVisibility: {
                record_uuid: columnVisibility?.record_uuid,
                record_client_name: columnVisibility?.record_client_name,
            },
            columnPinning: {
                left: ["record_date"],
                right: ["record_uuid"],
            },
        },

        muiTableProps: { elevation: 0 },

        muiTableContainerProps: { sx: { maxHeight: "70vh" } },

        muiTableHeadCellProps: { sx: { whiteSpace: "nowrap" } },

        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 260,
            },
        },

        state: {
            isLoading: isLoading,
            columnVisibility: columnVisibility,
        },

        localization:
            i18n.language === "id" ? MRT_Localization_ID : MRT_Localization_EN,

        muiTablePaperProps: { elevation: 0 },
    });

    const onSelectClient = (event) => {
        const selectedValue = event.target.value;
        setSelectedClient(selectedValue);

        if (table) {
            if (selectedValue === "") {
                table.getColumn("record_client_name")?.setFilterValue(undefined);
            } else {
                const selectedOption = optionClient.find(
                    (item) => item.company_client === selectedValue
                );

                if (selectedOption) {
                    table
                        .getColumn("record_client_name")
                        ?.setFilterValue(selectedOption.company_name);
                }
            }
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <PageHeader
                    title={t("general.read")}
                    labels={{ emission: t("menu.label21") }}
                    linkLast={false}
                    home={true}
                />

                <div className="card">
                    <div className="card-header">
                        <div className="flex d-flex justify-content-between">
                            <div className="gap-2 flex-wrap d-flex">
                                {account ? (
                                    <Link to={"/emission/create"} className="btn btn-success">
                                        {t("general.btnCreate")}
                                    </Link>
                                ) : (
                                    ""
                                )}

                                {account["client_subscription"] > 0 ? (
                                    account ? (
                                        <Link
                                            to={"/emission/create-in-bulk"}
                                            className="btn btn-success"
                                        >
                                            {t("general.btnCreateInBulk")}
                                        </Link>
                                    ) : (
                                        ""
                                    )
                                ) : (
                                    ""
                                )}
                            </div>

                            <div>
                                {account?.["is_parent"] === 1 && optionClient.length > 0 ? (
                                    <select
                                        style={{
                                            width: "fit-content",
                                            padding: "0.3rem 2.1rem 0.3rem 0.9rem",
                                        }}
                                        onChange={onSelectClient}
                                        value={selectedClient}
                                        id="companyClient"
                                        name="companyClient"
                                        className="form-select form-select-sm"
                                    >
                                        <option value="">{t('general.all')}</option>
                                        {optionClient?.map((item) => (
                                            <option
                                                key={item.company_client}
                                                value={item.company_client}
                                            >
                                                {item.company_name}
                                            </option>
                                        ))}
                                    </select>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="card-body">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div style={{ width: "100%", overflowX: "auto" }}>
                                <div style={{ minWidth: 1100 }}>
                                    <MaterialReactTable table={table} />
                                </div>
                            </div>
                        </LocalizationProvider>
                    </div>
                </div>
            </div>
        </div>
    );
}
